import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, Clock, MapPin, X, CheckCircle, Plus, Trash2, UserX, Printer } from 'lucide-react';
import { toast } from 'sonner';

const EVAL_TYPES = {
  sante: { label: 'Santé', color: '#DC2626', bg: '#FEF2F2' },
  police: { label: 'Police', color: '#2563EB', bg: '#EFF6FF' },
  pompiers: { label: 'Pompiers', color: '#EA580C', bg: '#FFF7ED' },
  au_choix: { label: 'Au choix', color: '#7C3AED', bg: '#F5F3FF' },
};

const DAY_FMT = { weekday: 'long', day: 'numeric', month: 'long' };

const formatDay = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const s = new Date(y, m - 1, d).toLocaleDateString('fr-CA', DAY_FMT);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const slotDate = (s) => {
  const [y, m, d] = s.date.split('-').map(Number);
  const [h, mi] = s.start_time.split(':').map(Number);
  return new Date(y, m - 1, d, h, mi);
};

const TypeBadge = ({ type }) => {
  const t = EVAL_TYPES[type] || EVAL_TYPES.au_choix;
  return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: t.color, backgroundColor: t.bg }}>{t.label}</span>;
};

// ---- Booking dialog with optional comment ----
const BookDialog = ({ slot, onConfirm, onClose, busy }) => {
  const [comment, setComment] = useState('');
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl p-5 w-full max-w-md" data-testid="book-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold" style={{ color: '#002768' }}>Réserver cette plage</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="book-dialog-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 border text-sm mb-3">
          <p className="font-semibold text-gray-800">{formatDay(slot.date)} — {slot.start_time} ({slot.duration_minutes} min)</p>
          <div className="flex items-center gap-2 mt-1">
            <TypeBadge type={slot.eval_type} />
            {slot.location && <span className="text-xs text-gray-500"><MapPin className="inline w-3 h-3" /> {slot.location}</span>}
          </div>
        </div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Commentaire pour l'enseignant (optionnel)</label>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Ex. : j'aimerais être évalué(e) sur un appel Santé…"
          className="w-full text-sm p-2 rounded-lg border focus:outline-none focus:border-blue-400 resize-none"
          data-testid="book-comment-input"
        />
        <button
          onClick={() => onConfirm(comment)}
          disabled={busy}
          className="w-full mt-3 py-2.5 rounded-lg font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: '#002768' }}
          data-testid="book-confirm-btn"
        >
          {busy ? 'Réservation…' : 'Confirmer ma réservation'}
        </button>
      </div>
    </div>
  );
};

// ---- Admin: slot creation form ----
const AdminCreateForm = ({ onCreated }) => {
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '', duration_minutes: 20, eval_type: 'au_choix', location: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.date || !form.start_time) { toast.error('Date et heure de début requises'); return; }
    setBusy(true);
    try {
      const resp = await axios.post(`${API}/eval-slots`, form);
      toast.success(`${resp.data.created} plage(s) créée(s)`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de la création');
    } finally { setBusy(false); }
  };
  return (
    <Card className="glass border-0" data-testid="admin-create-slots">
      <CardHeader className="py-3 px-4"><CardTitle className="text-sm" style={{ color: '#002768' }}><Plus className="inline w-4 h-4 mr-1" />Créer des plages</CardTitle></CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <label className="text-[11px] font-semibold text-gray-500">Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className="w-full text-sm p-2 rounded-lg border" data-testid="slot-date-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-500">Début</label>
            <input type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} className="w-full text-sm p-2 rounded-lg border" data-testid="slot-start-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-500">Fin (série, optionnel)</label>
            <input type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)} className="w-full text-sm p-2 rounded-lg border" data-testid="slot-end-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-500">Durée (min)</label>
            <input type="number" min="5" step="5" value={form.duration_minutes} onChange={e => set('duration_minutes', Number(e.target.value))} className="w-full text-sm p-2 rounded-lg border" data-testid="slot-duration-input" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-500">Type</label>
            <select value={form.eval_type} onChange={e => set('eval_type', e.target.value)} className="w-full text-sm p-2 rounded-lg border bg-white" data-testid="slot-type-select">
              {Object.entries(EVAL_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-500">Local</label>
            <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="Ex. B-2245" className="w-full text-sm p-2 rounded-lg border" data-testid="slot-location-input" />
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Astuce : avec une heure de fin, les plages sont générées en série (ex. 9:00 → 12:00 par 20 min = 9 plages).</p>
        <button onClick={submit} disabled={busy} className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: '#002768' }} data-testid="create-slots-btn">
          {busy ? 'Création…' : 'Créer'}
        </button>
      </CardContent>
    </Card>
  );
};

export default function Appointments({ studentInfo, isAdmin }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookTarget, setBookTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const myNumber = studentInfo?.student_number || '';
  const myName = studentInfo ? `${studentInfo.first_name || ''} ${studentInfo.last_name || ''}`.trim() : '';

  const load = useCallback(() => {
    axios.get(`${API}/eval-slots`)
      .then(r => setSlots(r.data || []))
      .catch(() => toast.error('Erreur de chargement des plages'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const myBooking = slots.find(s => s.booked_by?.student_number === myNumber);
  const canCancel = myBooking && (slotDate(myBooking) - Date.now()) > 24 * 3600 * 1000;

  const book = async (comment) => {
    setBusy(true);
    try {
      await axios.post(`${API}/eval-slots/${bookTarget.id}/book`, { student_number: myNumber, student_name: myName, comment });
      toast.success('Rendez-vous réservé!');
      setBookTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur de réservation');
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm('Annuler votre rendez-vous?')) return;
    try {
      await axios.post(`${API}/eval-slots/${myBooking.id}/cancel`, { student_number: myNumber });
      toast.success('Rendez-vous annulé');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const adminUnbook = async (slot) => {
    if (!window.confirm(`Libérer la plage de ${slot.booked_by?.student_name}?`)) return;
    await axios.post(`${API}/eval-slots/${slot.id}/unbook`).catch(() => {});
    load();
  };

  const adminDelete = async (slot) => {
    if (!window.confirm('Supprimer cette plage?')) return;
    await axios.delete(`${API}/eval-slots/${slot.id}`).catch(() => {});
    load();
  };

  const printDay = (date, daySlots) => {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Autorisez les popups'); return; }
    const esc = (t) => String(t || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const rows = daySlots.map(s => `<tr>
      <td style="padding:6px 10px;border:1px solid #e2e8f0;font-family:monospace;">${s.start_time}</td>
      <td style="padding:6px 10px;border:1px solid #e2e8f0;">${EVAL_TYPES[s.eval_type]?.label || s.eval_type}</td>
      <td style="padding:6px 10px;border:1px solid #e2e8f0;">${esc(s.location)}</td>
      <td style="padding:6px 10px;border:1px solid #e2e8f0;font-weight:600;">${esc(s.booked_by?.student_name) || '<span style="color:#94a3b8;font-weight:400;">— libre —</span>'}</td>
      <td style="padding:6px 10px;border:1px solid #e2e8f0;font-size:12px;color:#475569;">${esc(s.booked_by?.comment)}</td>
      <td style="padding:6px 10px;border:1px solid #e2e8f0;width:120px;"></td>
    </tr>`).join('');
    w.document.write(`<!DOCTYPE html><html><head><title>Évaluations — ${date}</title></head>
      <body style="font-family:Segoe UI,Arial,sans-serif;padding:30px;color:#1e293b;">
      <h1 style="font-size:18px;color:#002768;">Évaluations sur rendez-vous — ${formatDay(date)}</h1>
      <table style="border-collapse:collapse;width:100%;margin-top:12px;font-size:14px;">
      <tr style="background:#f1f5f9;"><th style="padding:6px 10px;border:1px solid #e2e8f0;">Heure</th><th style="padding:6px 10px;border:1px solid #e2e8f0;">Type</th><th style="padding:6px 10px;border:1px solid #e2e8f0;">Local</th><th style="padding:6px 10px;border:1px solid #e2e8f0;">Étudiant(e)</th><th style="padding:6px 10px;border:1px solid #e2e8f0;">Commentaire</th><th style="padding:6px 10px;border:1px solid #e2e8f0;">Note / résultat</th></tr>
      ${rows}</table></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const byDate = slots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const dates = Object.keys(byDate).sort();

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-4" data-testid="appointments-page">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-6 h-6" style={{ color: '#002768' }} />
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#002768' }}>Rendez-vous d'évaluation</h1>
          <p className="text-xs text-gray-500">Réservez votre plage pour l'évaluation de prise d'appel avec votre enseignant.</p>
        </div>
      </div>

      {isAdmin && <AdminCreateForm onCreated={load} />}

      {/* My booking */}
      {myBooking && (
        <Card className="border-2" style={{ borderColor: '#16A34A', backgroundColor: '#F0FDF4' }} data-testid="my-booking-card">
          <CardContent className="pt-4 pb-4 flex flex-wrap items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-bold text-green-800">Votre rendez-vous : {formatDay(myBooking.date)} à {myBooking.start_time}</p>
            <p className="text-xs text-green-700">
              {EVAL_TYPES[myBooking.eval_type]?.label} • {myBooking.duration_minutes} min{myBooking.location ? ` • Local ${myBooking.location}` : ''}
              {myBooking.booked_by?.comment ? ` • « ${myBooking.booked_by.comment} »` : ''}
            </p>
          </div>
          {canCancel ? (
            <button onClick={cancel} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50" data-testid="cancel-booking-btn">
              Annuler
            </button>
          ) : (
            <span className="text-[11px] text-gray-500 italic">Annulation impossible à moins de 24 h — contactez votre enseignant</span>
          )}
          </CardContent>
        </Card>
      )}

      {/* Slots grouped by day */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-8">Chargement…</p>
      ) : dates.length === 0 ? (
        <Card className="glass border-0"><CardContent className="py-10 text-center text-sm text-gray-400" data-testid="no-slots-msg">
          Aucune plage disponible pour le moment. Revenez plus tard!
        </CardContent></Card>
      ) : (
        dates.map(date => (
          <Card key={date} className="glass border-0" data-testid={`day-${date}`}>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm" style={{ color: '#002768' }}>{formatDay(date)}</CardTitle>
                {isAdmin && (
                  <button onClick={() => printDay(date, byDate[date])} className="text-xs text-gray-400 hover:text-gray-600" data-testid={`print-day-${date}`}>
                    <Printer className="inline w-3.5 h-3.5 mr-1" />Liste du jour
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {byDate[date].map(s => {
                const taken = !!s.booked_by;
                const isMine = s.booked_by?.student_number === myNumber;
                return (
                  <div key={s.id}
                    className={`p-3 rounded-lg border-2 transition-all ${taken ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200 hover:border-green-400 cursor-pointer'}`}
                    onClick={!taken && !myBooking ? () => setBookTarget(s) : undefined}
                    data-testid={`slot-${s.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-bold" style={{ color: taken ? '#9CA3AF' : '#002768' }}>
                        <Clock className="inline w-3.5 h-3.5 mr-1" />{s.start_time}
                      </span>
                      <TypeBadge type={s.eval_type} />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-400">{s.duration_minutes} min{s.location ? ` • ${s.location}` : ''}</span>
                      {taken ? (
                        <span className={`text-xs font-semibold ${isMine ? 'text-green-600' : 'text-gray-400'}`}>
                          {isAdmin || isMine ? s.booked_by.student_name : 'Réservé'}
                        </span>
                      ) : myBooking ? (
                        <span className="text-[11px] text-gray-300 italic">1 réservation max</span>
                      ) : (
                        <span className="text-xs font-semibold text-green-600">Libre — cliquez</span>
                      )}
                    </div>
                    {isAdmin && s.booked_by?.comment && (
                      <p className="text-[11px] text-gray-500 italic mt-1">« {s.booked_by.comment} »</p>
                    )}
                    {isAdmin && (
                      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                        {taken && (
                          <button onClick={(e) => { e.stopPropagation(); adminUnbook(s); }} className="text-[11px] text-amber-600 hover:text-amber-800 font-semibold" data-testid={`unbook-${s.id}`}>
                            <UserX className="inline w-3 h-3 mr-0.5" />Libérer
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); adminDelete(s); }} className="text-[11px] text-red-500 hover:text-red-700 font-semibold" data-testid={`delete-${s.id}`}>
                          <Trash2 className="inline w-3 h-3 mr-0.5" />Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))
      )}

      {bookTarget && <BookDialog slot={bookTarget} busy={busy} onConfirm={book} onClose={() => setBookTarget(null)} />}
    </div>
  );
}
